import { FC, useRef, useEffect, useState } from "react";
import {
  Autocomplete,
  Box,
  Checkbox,
  Chip,
  CircularProgress,
  createFilterOptions,
  FormControlLabel,
  TextField,
  Paper,
  Typography,
  IconButton,
} from "@mui/material";

import CloseIcon from "@mui/icons-material/Close";

import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import { useTranslation } from "react-i18next";
import { ValuesSelected } from "../../../../../../../../redux/slices/dataTable/dataTableFilters";
import { AutocompleteStyles } from "./AutoCompleteStyles";

const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
const checkedIcon = <CheckBoxIcon fontSize="small" />;

const AutocompleteDataFilters = (
  typeOfAutocomplete,
  dataOptions,
  allValuesSelected,
  valuesSelected,
  loading,
  setSelectAll,
  resetSelectAll,
  managingSelectOption,
  setIsOpenFilters,
  isOpenFilters,
) => {
  const [t] = useTranslation("global");
  const autocompleteRef = useRef<HTMLDivElement>(null);

  const [menuPosition, setMenuPosition] = useState({ top: 0, left: 0 });
  const [showChipsMenu, setShowChipsMenu] = useState(false);
  const [autocompleteOpen, setAutocompleteOpen] = useState(false);

  const TypeOfLabel = () => {
    if (typeOfAutocomplete === "locations") {
      return t("Data.Search_sites");
    }
    if (typeOfAutocomplete === "actions") {
      return t("Data.Search_actions");
    }
    if (typeOfAutocomplete === "users") {
      return t("Data.Search_users");
    }
  };

  const filterName = () => {
    if (typeOfAutocomplete === "locations") {
      return t("Data.Sites");
    }
    if (typeOfAutocomplete === "actions") {
      return t("Data.Actions");
    }
    if (typeOfAutocomplete === "users") {
      return t("Fleet_users.table.My_users");
    }
  };

  const transform =
    Array.isArray(dataOptions) && dataOptions.length >= 1
      ? dataOptions?.map((c) => c)
      : [];

  // Helper function to get unique key for any option
  const getUniqueKey = (option: any, index?: number) => {
    if (typeOfAutocomplete === "actions") {
      return (
        option.userFacingNameTextId ||
        option.id ||
        `${option.name}_${index || 0}`
      );
    }
    return option.id || `${option.name}_${index || 0}`;
  };

  let filteredDataOptions = Array.isArray(dataOptions) ? dataOptions : [];
  if (typeOfAutocomplete === "actions") {
    const uniqueActions = new Map();

    // Ensure dataOptions is an array before using forEach
    if (Array.isArray(dataOptions)) {
      dataOptions.forEach((action, index) => {
        const key =
          (action as any).userFacingNameTextId !== undefined
            ? (action as any).userFacingNameTextId
            : `${action.name}_${index}`;
        if (!uniqueActions.has(key)) {
          uniqueActions.set(key, action);
        }
      });

      filteredDataOptions = Array.from(uniqueActions.values());
    } else {
      // If dataOptions is not an array, initialize as empty array
      filteredDataOptions = [];
    }
  }

  useEffect(() => {
    const hasValues = valuesSelected.length > 0;
    const shouldShowMenu = hasValues || autocompleteOpen;
    setShowChipsMenu(shouldShowMenu);

    if (shouldShowMenu && autocompleteRef.current) {
      const rect = autocompleteRef.current.getBoundingClientRect();

      const topOffset = typeOfAutocomplete === "actions" ? -150 : 0;

      setMenuPosition({
        top: rect.top + topOffset,
        left: rect.right + 10,
      });
    }
  }, [typeOfAutocomplete, valuesSelected.length, autocompleteOpen]);

  const handleRemoveChip = (optionToRemove: ValuesSelected) => {
    const newValues = valuesSelected?.filter((item) => {
      if (typeOfAutocomplete === "actions") {
        return (
          item.userFacingNameTextId !== optionToRemove.userFacingNameTextId
        );
      }
      return item.id !== optionToRemove.id;
    });
    managingSelectOption(newValues);
  };
  const sideMenuStyle = {
    ...AutocompleteStyles.sideMenuStyle,
    top: menuPosition.top,
    left: menuPosition.left,
  };

  return (
    <div style={{ position: "relative" }}>
      <div ref={autocompleteRef}>
        <span className="titles-sb-features">{filterName()}</span>
      </div>
      <Autocomplete
        sx={{
          ...AutocompleteStyles.Autocomplete,
          overflowY: valuesSelected?.length > 2 ? "scroll" : "hidden",
          height: valuesSelected?.length > 2 ? "80px" : "60px",
        }}
        open={autocompleteOpen}
        onOpen={() => setAutocompleteOpen(true)}
        onClose={() => setAutocompleteOpen(false)}
        filterOptions={(options, params) => {
          const filter = createFilterOptions();
          const filtered = filter(options, params as any);
          return [
            { name: t("Dashboard.setAllLocations"), id: 0 },
            ...filtered,
          ] as any;
        }}
        disabled={loading}
        className="autocomplete-dashboard mb-5"
        multiple
        loading={loading}
        disableListWrap={true}
        options={filteredDataOptions}
        disableCloseOnSelect
        value={valuesSelected}
        getOptionLabel={(option: ValuesSelected) => {
          if (typeOfAutocomplete === "actions") {
            return `${option.name} ${option.cerealType || ""}`;
          }
          return option.name;
        }}
        isOptionEqualToValue={(option, value) => {
          if (typeOfAutocomplete === "actions") {
            return (
              (option as any).userFacingNameTextId ===
              (value as any).userFacingNameTextId
            );
          }
          return option.id === value.id;
        }}
        onChange={(_e, value, reason) => {
          if (reason === "clear" || reason === "removeOption") resetSelectAll();
          if (
            reason === "selectOption" &&
            value.length === filteredDataOptions.length
          )
            setSelectAll(
              typeOfAutocomplete === "actions"
                ? filteredDataOptions
                : transform,
            );
          managingSelectOption(value);
        }}
        renderInput={(params) => (
          <TextField
            sx={AutocompleteStyles.AutoCompleteTextField}
            {...params}
            placeholder={TypeOfLabel()}
          />
        )}
        renderOption={(props, option, { selected }) => {
          if (option.id === 0) {
            return (
              <Box
                key={option.id}
                sx={AutocompleteStyles.BoxOptions}
                onMouseDown={(e) => e.preventDefault()}
                pl={1.5}
                py={0.5}
              >
                <FormControlLabel
                  onClick={(e) => {
                    if (filteredDataOptions.length >= 1) {
                      e.preventDefault();
                      !allValuesSelected
                        ? setSelectAll(transform)
                        : resetSelectAll();
                    }
                  }}
                  disabled={filteredDataOptions.length === 0 || loading}
                  label={t("Dashboard.setAllLocations")}
                  control={
                    <Checkbox
                      disabled={filteredDataOptions.length === 0 || loading}
                      checked={allValuesSelected}
                    />
                  }
                />
              </Box>
            );
          }

          if (loading) {
            return (
              <div className="circular-progress-components">
                <CircularProgress />
              </div>
            );
          } else {
            return (
              <li {...props} key={getUniqueKey(option)}>
                <Checkbox
                  icon={icon}
                  disabled={loading}
                  checkedIcon={checkedIcon}
                  style={{ marginRight: 8 }}
                  checked={
                    option.id === 0
                      ? !!(
                          valuesSelected?.filter((x) => x.id === undefined)
                            .length !==
                          (Array.isArray(dataOptions) ? dataOptions.length : 0)
                        )
                      : selected
                  }
                />

                <div>{`${
                  typeOfAutocomplete === "actions"
                    ? `${option.name} (${option.cerealType})`
                    : option.name
                }`}</div>
              </li>
            );
          }
        }}
        renderTags={(list) => {
          if (list.length === 1) {
            return (
              <Box sx={AutocompleteStyles.BoxRenderTags}>
                {list.map((value: any, index) => (
                  <Chip
                    key={getUniqueKey(value, index)}
                    label={
                      value.name.length > 10
                        ? `${value.name.slice(0, 13)}...`
                        : value.name
                    }
                  />
                ))}
              </Box>
            );
          }

          if (list.length > 1) {
            return (
              <Box sx={AutocompleteStyles.BoxRenderTags}>
                <Chip
                  label={`${
                    list[0].name.length > 10
                      ? `${list[0].name.slice(0, 13)}...`
                      : list[0].name
                  } ${
                    list.length >= 2
                      ? `+ ${list.length - 1} ${t("Common_options.others")}`
                      : ""
                  }  `}
                />
              </Box>
            );
          }
        }}
        renderGroup={(params) => params as unknown as React.ReactNode}
      />

      {autocompleteOpen &&
        valuesSelected &&
        valuesSelected.length > 0 &&
        menuPosition.top !== 0 &&
        menuPosition.left !== 0 && (
          <Paper
            sx={sideMenuStyle}
            elevation={3}
            onMouseDown={(e) => e.preventDefault()}
            onClick={(e) => e.stopPropagation()}
          >
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              mb={2}
            >
              <Typography variant="h6" component="h2">
                {filterName()}
              </Typography>
              <IconButton size="small" onClick={() => setShowChipsMenu(false)}>
                <CloseIcon />
              </IconButton>
            </Box>

            <Box
              sx={{
                display: "flex",
                flexWrap: "wrap",
                gap: 1,
                maxHeight: "300px",
                overflow: "auto",
                flex: 1,
                paddingRight: 1,
              }}
              onMouseDown={(e) => e.preventDefault()}
              onClick={(e) => e.stopPropagation()}
            >
              {valuesSelected &&
                valuesSelected.length > 0 &&
                valuesSelected.map((option, index) => (
                  <Chip
                    key={getUniqueKey(option, index)}
                    label={option.name}
                    onDelete={() => handleRemoveChip(option)}
                    color="primary"
                    variant="outlined"
                    size="small"
                  />
                ))}
            </Box>
          </Paper>
        )}
    </div>
  );
};
export default AutocompleteDataFilters;
